import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { Cell, Column } from "react-table";
import styled from "styled-components";
import { Button, Spinner } from "@blueprintjs/core";

import {
  AppToaster,
  DataTable,
  fuzzyTextFilter,
  MenuSelect,
} from "@components";
import { useCreateOrganization, useInvitations, useMembers } from "@hooks";
import { UserContext } from "@providers";
import { OrgData } from "@types";
import { Feature } from "@utils";

import { patchRbacRole } from "@pages/settings/api";
import { RbacRole } from "@pages/settings/types";

import { NewTeamMemberDialog } from "../NewTeamMemberDialog";

interface Props {
  org: OrgData;
  hasTeamTier: boolean;
}

const Container = styled.div`
  position: relative;
  width: 600px;
  padding-top: 20px;
`;

const Email = styled.div`
  font-size: var(--global-font-size-xs);
  color: var(--link-color);
`;

const NameCell = styled.div`
  font-size: var(--global-font-size-s);
  color: var(--global-black);
`;

const StyledCell = styled.div`
  padding-top: 2px;
`;

const RoleCell = styled.div`
  color: var(--global-black);
`;

const humanReadableStatuses: { [key: string]: string } = {
  pending_invite: "processing invitation",
  invited: "pending invitation",
  registering: "registered, but hasn't accepted invitation",
  logged_in: "logged in, but hasn't accepted invitation",
  rejected: "invitation rejected",
  timeout: "invitation expired",
};

const tableColumns = (
  deployment_id: string,
  user_name: string,
  refresh: () => void,
  is_interactive: boolean = false
): Column<ShownUser>[] => [
  {
    id: "name",
    accessor: (user) => `${user.name} ${user.login}`,
    Header: "name",
    Cell: ({ row }: Cell<ShownUser>) => {
      return (
        <NameCell>
          <span data-testid="member-name">
            {row.original.name || row.original.login}
          </span>
          <Email data-testid="member-email">{row.original.email}</Email>
        </NameCell>
      );
    },
  },
  {
    id: "auth",
    accessor: "authentication",
    Header: "auth",
    Cell: ({ value }) => {
      return <StyledCell>{value}</StyledCell>;
    },
  },
  {
    id: "since",
    accessor: "since",
    Header: "joined",
    Cell: ({ value, row }) => {
      if (row.original.invitation_status) {
        const status = row.original.invitation_status;
        const expireMessage =
          status !== "rejected"
            ? `(expires ${moment(
                row.original.invitation_expiration_time
              ).fromNow()})`
            : "";

        return (
          <StyledCell>
            {humanReadableStatuses[status] || status} {expireMessage}
          </StyledCell>
        );
      }
      const tzAdjusted = moment.utc(value).local();
      return <StyledCell>{moment(tzAdjusted).fromNow()}</StyledCell>;
    },
  },
  {
    id: "role",
    accessor: "role",
    Header: "role",
    Cell: ({ value, row }) => {
      if (is_interactive) {
        return (
          <MenuSelect<RbacRole>
            items={[RbacRole.Admin, RbacRole.Member]}
            onItemSelect={(new_role) => {
              patchRbacRole(deployment_id, row.original.id, new_role)
                .then(() => {
                  refresh();
                })
                .catch(() =>
                  AppToaster.show({
                    message: "Failed to update user role",
                    intent: "danger",
                  })
                );
            }}
            extractor={(role) => `${role}`}
            isItemSelected={(role) => role === value}
            filterable={false}
            disabled={row.original.login === user_name}
          >
            <Button
              disabled={row.original.login === user_name}
              minimal
              small
              rightIcon="caret-down"
            >
              {value}
            </Button>
          </MenuSelect>
        );
      }
      return <RoleCell>{value}</RoleCell>;
    },
  },
];

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  right: 0px;
`;

type ShownUser = {
  id: number;
  name?: string;
  email: string;
  login?: string;
  role?: RbacRole;
  since?: string;
  authentication?: string;

  invitation_expiration_time?: string;
  invitation_status?: string;
};

export const TeamRbacPanel: React.FC<Props> = ({ org, hasTeamTier }) => {
  const { user } = useContext(UserContext);
  const [inviteMemberDialogOpen, setInviteMemberDialogOpen] =
    useState<boolean>(false);

  const members = useMembers();
  const activeInvitationsRes = useInvitations();

  const { createOrganization } = useCreateOrganization();
  const parentOrgId = org.organization_id;

  const users: ShownUser[] = [
    ...(members.data || []),
    ...(activeInvitationsRes.data?.user_invites
      .filter((invite) => {
        return invite.status !== "joined";
      })
      .map((invite) => {
        return {
          id: invite.id,
          name: invite.invited_email.split("@")[0],
          email: invite.invited_email,
          invitation_expiration_time: invite.expiration_time,
          invitation_status: invite.status,
          role: invite.initial_role,
        };
      }) || []),
  ];
  const usersLoading = members.isLoading || activeInvitationsRes.isLoading;

  // Create the parent organization if it doesn't exist, this is just temporary, not all users have parent org
  useEffect(() => {
    if (!org.id || org.organization_id) return;

    createOrganization(org.id);
  }, [parentOrgId, createOrganization, org]);

  return (
    <Container>
      <Header>
        {Feature.hasInvites({ org }) && (
          <Button
            intent="primary"
            onClick={() => {
              setInviteMemberDialogOpen(true);
            }}
          >
            Invite member
          </Button>
        )}
      </Header>
      {Feature.hasInvites({ org }) && (
        <NewTeamMemberDialog
          deployment={org}
          isOpen={inviteMemberDialogOpen}
          onClose={() => setInviteMemberDialogOpen(false)}
        />
      )}
      {usersLoading ? (
        <Spinner />
      ) : (
        <DataTable
          data={users}
          columns={tableColumns(
            org.id,
            user!.username,
            () => members.refetch(),
            hasTeamTier && org.has_rbac
          )}
          sorting={{
            initialId: "name",
          }}
          filter={fuzzyTextFilter}
        />
      )}
    </Container>
  );
};
